import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import {
  Button,
  Drawer,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core'
import ModalDialog from '../common/ModalDialog'
import { EditableSelect } from '../common/EditableComponents'
import { CustomTooltip } from '../common/Tooltip'
import { getNmCcdProviderErrorMessage, validateNmCcdForm } from '../../utils/nmCcdCompliance'

const emptyForm = (shop = {}) => ({
  shopId: shop.id,
  companyId: shop.companyId,
  complianceType: 'NM_CCD',
  stateCode: 'NM',
  environment: 'Development',
  nmCcdOrganizationId: '',
  nmCcdLocationId: '',
  nmCcdJwtTokenLast4: '',
  nmCcdJwtTokenConfigured: false,
  enabled: false,
  enableSync: false,
})

const environmentOptions = [
  { value: 'Development', label: 'Development' },
  { value: 'UAT', label: 'UAT' },
  { value: 'Production', label: 'Production' },
]

const NmCcdAccountRow = ({
  account = {},
  shop = {},
  onSave,
  onDelete,
  onTest,
  notifySuccess = () => {},
  notifyError = () => {},
  canManage = true,
  canEnableNmCcd = false,
}) => {
  const initial = Object.keys(account).length ? { ...emptyForm(shop), ...account } : emptyForm(shop)
  const [form, setForm] = useState(initial)
  const [persistedIdentity, setPersistedIdentity] = useState(
    Object.keys(account).length ? account : null,
  )
  const [jwtTokenInput, setJwtTokenInput] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [isEditing, setIsEditing] = useState(!account.id)
  const [validationResult, setValidationResult] = useState(null)
  const [isClient, setIsClient] = useState(false)
  const deleteAccountModal = useRef()
  const isMounted = useRef(true)

  useEffect(() => {
    setIsClient(true)
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    const next = Object.keys(account).length ? { ...emptyForm(shop), ...account } : emptyForm(shop)
    setForm(next)
    setPersistedIdentity(Object.keys(account).length ? account : null)
    setJwtTokenInput('')
    setFieldErrors({})
    setIsEditing(!account.id)
    setValidationResult(null)
  }, [account, shop.id])

  const update = (key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value }
      if (key === 'enabled' && !value) next.enableSync = false
      return next
    })
    setValidationResult(null)
    setFieldErrors((current) => ({ ...current, [key]: undefined, jwtToken: undefined }))
  }

  const save = () => {
    const tokenOperation = jwtTokenInput.trim() ? 'replace' : 'preserve'
    const errors = validateNmCcdForm(form, jwtTokenInput, persistedIdentity)
    if (Object.keys(errors).length) {
      setFieldErrors(errors)
      return Promise.resolve(false)
    }
    setIsSaving(true)
    return onSave(form, jwtTokenInput, tokenOperation)
      .then((saved) => {
        setJwtTokenInput('')
        setForm({ ...emptyForm(shop), ...saved })
        setPersistedIdentity(saved)
        notifySuccess('Configuration saved')
        setIsEditing(false)
        return true
      })
      .catch((error) => {
        notifyError(getNmCcdProviderErrorMessage(error))
        return false
      })
      .finally(() => setIsSaving(false))
  }

  const deleteAccount = () => deleteAccountModal.current.handleClickOpen()

  const confirmDeleteAccount = () => {
    if (!form.id) return Promise.resolve()
    deleteAccountModal.current.handleClose()
    setIsSaving(true)
    return onDelete(form.id)
      .then(() => notifySuccess('NM CCD compliance account deleted.'))
      .catch(() => notifyError('NM CCD could not delete this compliance account.'))
      .finally(() => {
        if (isMounted.current) setIsSaving(false)
      })
  }

  const testConnection = () => {
    const errors = validateNmCcdForm({ ...form, enabled: false }, jwtTokenInput, persistedIdentity)
    if (Object.keys(errors).length) {
      setFieldErrors(errors)
      return
    }
    setIsTesting(true)
    onTest(form, jwtTokenInput)
      .then((result) => {
        if (result && result.success === false) {
          notifyError(getNmCcdProviderErrorMessage(result))
          return
        }
        if (result && result.success === true) setValidationResult(result)
        notifySuccess('NM CCD connection verified. Configuration was not changed.')
      })
      .catch((error) => notifyError(getNmCcdProviderErrorMessage(error)))
      .finally(() => setIsTesting(false))
  }

  const canTest =
    !isTesting &&
    !isSaving &&
    !Object.keys(validateNmCcdForm({ ...form, enabled: false }, jwtTokenInput, persistedIdentity))
      .length
  const cancelEdit = () => {
    setForm(Object.keys(account).length ? { ...emptyForm(shop), ...account } : emptyForm(shop))
    setJwtTokenInput('')
    setFieldErrors({})
    setIsEditing(!account.id)
  }

  const field = (key, options = {}) => {
    if (!isEditing && key === 'jwtToken') {
      return form.nmCcdJwtTokenLast4 ? `••••${form.nmCcdJwtTokenLast4}` : 'Not configured'
    }
    if (!isEditing) return String(form[key] || '')
    return (
      <React.Fragment>
        <TextField
          type={options.type || 'text'}
          value={key === 'jwtToken' ? jwtTokenInput : form[key]}
          fullWidth={key === 'jwtToken'}
          placeholder={options.placeholder}
          onChange={(event) =>
            key === 'jwtToken'
              ? setJwtTokenInput(event.target.value)
              : update(key, event.target.value)
          }
          error={Boolean(fieldErrors[key])}
          inputProps={{
            ...(options.numeric ? { inputMode: 'numeric' } : {}),
            'aria-label': options.label,
          }}
        ></TextField>
        {fieldErrors[key] ? <div role="alert">{fieldErrors[key]}</div> : null}
      </React.Fragment>
    )
  }

  return (
    <React.Fragment key={form.id || form.shopId}>
      <TableRow>
        <TableCell>{shop.name || ''}</TableCell>
        <TableCell>
          <EditableSelect
            valueToRender={form.environment}
            editMode={isEditing}
            value={form.environment}
            onChange={(event) => update('environment', event.target.value)}
            inputProps={{ 'aria-label': 'Environment' }}
            className=""
          >
            {environmentOptions.map((environment) => (
              <MenuItem key={environment.value} value={environment.value}>
                {environment.label}
              </MenuItem>
            ))}
          </EditableSelect>
          {fieldErrors.environment ? <div role="alert">{fieldErrors.environment}</div> : null}
        </TableCell>
        <TableCell>
          {field('nmCcdOrganizationId', { numeric: true, label: 'Organization ID' })}
        </TableCell>
        <TableCell>{field('nmCcdLocationId', { numeric: true, label: 'Location ID' })}</TableCell>
        <TableCell>
          {isEditing ? (
            <Grid container alignItems="center" spacing={8} wrap="nowrap">
              <Grid item xs>
                {field('jwtToken', { type: 'password', label: 'JWT Token' })}
              </Grid>
              {form.nmCcdJwtTokenLast4 ? (
                <Grid item>
                  <CustomTooltip
                    message={`Leave blank to keep the saved token (••••${form.nmCcdJwtTokenLast4}).`}
                  />
                </Grid>
              ) : null}
            </Grid>
          ) : (
            field('jwtToken', { type: 'password', label: 'JWT Token' })
          )}
        </TableCell>
        <TableCell>
          <Switch
            checked={Boolean(form.enabled)}
            disabled={!isEditing || !canEnableNmCcd}
            onChange={(event) => update('enabled', event.target.checked)}
            inputProps={{ 'aria-label': 'Enabled' }}
          />
        </TableCell>
        <TableCell>
          <Switch
            checked={Boolean(form.enableSync)}
            disabled={!isEditing || !form.enabled || !canEnableNmCcd}
            onChange={(event) => update('enableSync', event.target.checked)}
            inputProps={{ 'aria-label': 'Enable Sync' }}
          />
        </TableCell>
        <TableCell>
          {canManage ? (
            <Grid container spacing={8} alignItems="center">
              {isEditing ? (
                <React.Fragment>
                  <Grid item>
                    <Button className="primary-button" disabled={!canTest} onClick={testConnection}>
                      {isTesting ? 'Validating…' : 'Validate'}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button className="primary-button" disabled={isSaving} onClick={() => save()}>
                      {isSaving ? 'Saving…' : 'Save'}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      className="danger-button"
                      disabled={isSaving || isTesting}
                      onClick={cancelEdit}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Grid item>
                    <Button className="primary-button" disabled={!canTest} onClick={testConnection}>
                      {isTesting ? 'Validating…' : 'Validate'}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button className="primary-button" onClick={() => setIsEditing(true)}>
                      Edit
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      className="danger-button"
                      disabled={isSaving || !form.id}
                      onClick={deleteAccount}
                    >
                      Delete
                    </Button>
                  </Grid>
                </React.Fragment>
              )}
            </Grid>
          ) : null}
        </TableCell>
      </TableRow>
      <Drawer
        anchor="right"
        open={Boolean(validationResult)}
        onClose={() => setValidationResult(null)}
      >
        <Grid style={{ margin: 16, minWidth: 360, maxWidth: 480 }}>
          <h3>NM CCD validation result</h3>
          <p>
            <strong>Shop:</strong> {shop.name || ''}
          </p>
          <p>
            <strong>Organization:</strong>{' '}
            {(validationResult &&
              validationResult.metadata &&
              (validationResult.metadata.organizationName || validationResult.metadata.name)) ||
              ''}
          </p>
          <p>
            <strong>Location ID:</strong>{' '}
            {(validationResult &&
              validationResult.metadata &&
              validationResult.metadata.locationId) ||
              ''}
            {validationResult &&
            validationResult.metadata &&
            String(validationResult.metadata.locationId) === String(form.nmCcdLocationId) ? (
              <i
                className="material-icons"
                aria-label="Location ID matches current configuration"
                style={{ color: '#4caf50', fontSize: 20, marginLeft: 8, verticalAlign: 'middle' }}
              >
                check
              </i>
            ) : null}
          </p>
          <p>
            <strong>Address:</strong>{' '}
            {(validationResult && validationResult.metadata && validationResult.metadata.address) ||
              ''}
          </p>
          <p>
            <strong>POS API status:</strong>{' '}
            {(validationResult &&
              validationResult.metadata &&
              validationResult.metadata.posApiConnectionStatus) ||
              ''}
          </p>
          <p>
            <strong>Checked at:</strong> {(validationResult && validationResult.checkedAt) || ''}
          </p>
        </Grid>
      </Drawer>
      {isClient
        ? ReactDOM.createPortal(
            <ModalDialog
              ref={deleteAccountModal}
              title="Delete NM CCD Account"
              confirmText="Delete"
              onConfirm={confirmDeleteAccount}
            >
              Delete this NM CCD compliance account?
            </ModalDialog>,
            document.body,
          )
        : null}
    </React.Fragment>
  )
}

const NmCcdAccount = ({ accounts, shops = [], account, shop, ...props }) => {
  const currentShopId = (shop || {}).id
  const configuredAccounts = Array.isArray(accounts)
    ? currentShopId &&
      !accounts.some((configuredAccount) => configuredAccount.shopId === currentShopId)
      ? [...accounts, account || {}]
      : accounts
    : account
    ? [account]
    : []
  return (
    <Paper className="paper-page">
      <div className="table-scroll">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ minWidth: 150 }}>Shop</TableCell>
              <TableCell style={{ minWidth: 150 }}>Environment</TableCell>
              <TableCell style={{ minWidth: 150 }}>Organization ID</TableCell>
              <TableCell style={{ minWidth: 150 }}>Location ID</TableCell>
              <TableCell style={{ minWidth: 180 }}>JWT Token</TableCell>
              <TableCell style={{ minWidth: 100 }}>Enabled</TableCell>
              <TableCell style={{ minWidth: 120 }}>Enable Sync</TableCell>
              <TableCell style={{ minWidth: 300 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {configuredAccounts.map((configuredAccount) => {
              const configuredShop = shops.find(
                (shopItem) => shopItem.id === configuredAccount.shopId,
              ) ||
                (shop && (!configuredAccount.shopId || shop.id === configuredAccount.shopId)
                  ? shop
                  : null) || {
                  id: configuredAccount.shopId,
                  name: configuredAccount.shopId,
                }
              return (
                <NmCcdAccountRow
                  key={
                    configuredAccount.id ||
                    configuredAccount.shopId ||
                    (shop && shop.id) ||
                    'new-nm-ccd-account'
                  }
                  account={configuredAccount}
                  shop={configuredShop}
                  canManage={
                    !configuredAccount.shopId || configuredAccount.shopId === currentShopId
                  }
                  {...props}
                />
              )
            })}
          </TableBody>
        </Table>
      </div>
    </Paper>
  )
}

export default NmCcdAccount



// WEBPACK FOOTER //
// ./src/components/Compliance/NmCcdAccount.js